/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef, useCallback } from "react";
import { observer } from "mobx-react";
import { makeApi, URLS } from "Utils/apiURL";
import CallsUtils from "Utils/CallsUtils";
import countryData from "country-telephone-data";
import Flags from "Assets/png/flags.png";
import PaginationComponent from "Components/common/PaginationComponent";
import PageLimit from "Components/common/PageLimit";
import "react-telephone-input/css/default.css";

// Sprite-based flag (renders consistently cross-platform, unlike emoji flags)
const CountryFlag = ({ country }) => {
  if (!country?.iso2) return null;
  return (
    <div className="react-tel-input" style={{ display: "inline-flex" }}>
      <div className={`flag ${country.iso2.toLowerCase()}`} style={{ backgroundImage: `url(${Flags})` }} />
    </div>
  );
};

// ─── helpers ─────────────────────────────────────────────────────────────────

const getCountryFromPhone = phoneNumber => {
  if (!phoneNumber) return null;
  const { allCountries, allCountryCodes, iso2Lookup } = countryData;
  const clean = phoneNumber.replace(/[^\d+]/g, "");
  const prefix = clean.replace(/^\+/, "").substr(0, 4);
  if (allCountryCodes[prefix]) {
    const iso2 = allCountryCodes[prefix][0];
    if (iso2 && iso2Lookup[iso2] !== undefined) return allCountries[iso2Lookup[iso2]];
  }
  let best = { dialCode: "", priority: 10001 };
  allCountries.forEach(c => {
    const n = clean.startsWith("+") ? clean.replace(/^\+/, "") : clean;
    const d = c.dialCode.replace(/^\+/, "");
    if (n.startsWith(d)) {
      if (d.length > best.dialCode.replace(/^\+/, "").length) best = c;
      else if (d.length === best.dialCode.replace(/^\+/, "").length && c.priority < best.priority) best = c;
    }
  });
  return best.priority === 10001 ? null : best;
};

const getInitials = m => {
  const first = (m.firstName || "")[0] || "";
  const last = (m.lastName || "")[0] || "";
  return (first + last).toUpperCase() || m.email?.[0]?.toUpperCase() || "?";
};
const getFullName = m => [m.firstName, m.lastName].filter(Boolean).join(" ") || m.email || "";

const fmtDate = ts => ts ? new Date(ts).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "";
const fmtTime = ts => ts ? new Date(ts).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true }) : "";

const formatDateTime = ts => {
  if (!ts) return "";
  return `${fmtDate(ts)}, ${fmtTime(ts)}`;
};

const exportCSV = (rows, filename) => {
  const esc = v => `"${String(v == null ? "" : v).replace(/"/g, '""')}"`;
  const csv = rows.map(r => r.map(esc).join(",")).join("\n");
  const a = Object.assign(document.createElement("a"), {
    href: URL.createObjectURL(new Blob([csv], { type: "text/csv" })),
    download: filename
  });
  a.click();
};

const DATE_PRESETS = ["Today", "Last 7 days", "Last 30 days", "Custom"];

const getPresetRange = preset => {
  const now = new Date();
  const start = new Date(now);
  if (preset === "Today") { start.setHours(0, 0, 0, 0); }
  else if (preset === "Last 7 days") { start.setDate(now.getDate() - 7); }
  else if (preset === "Last 30 days") { start.setDate(now.getDate() - 30); }
  else return null;
  return { start: start.toISOString().split("T")[0], end: now.toISOString().split("T")[0] };
};

// ─── sub-components ───────────────────────────────────────────────────────────

const SummaryCard = ({ label, value, sub }) => (
  <div className="ccuCard">
    <div className="ccuCard__label">{label}</div>
    <div className="ccuCard__value">{value}</div>
    {sub && <div className="ccuCard__sub">{sub}</div>}
  </div>
);

// Multi-select dropdown for team members, with an "All members" option
const MemberMultiSelect = ({ members, selected, onChange }) => {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  React.useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const allSelected = selected.length === 0;
  const toggle = id => onChange(selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id]);
  const label = allSelected
    ? "All members"
    : selected.length === 1
      ? getFullName(members.find(m => m.memberId === selected[0]) || {})
      : `${selected.length} members`;
  return (
    <div className="ccuMs" ref={ref}>
      <button type="button" className={`ccuMs__trigger ${open ? "ccuMs__trigger--open" : ""}`} onClick={() => setOpen(o => !o)}>
        <span className={allSelected ? "ccuMs__label" : "ccuMs__label ccuMs__label--active"}>{label}</span>
        <i className="material-icons" style={{ fontSize: 16, transform: open ? "rotate(180deg)" : "none" }}>expand_more</i>
      </button>
      {open && (
        <div className="ccuMs__menu">
          <button type="button" className={`ccuMs__row ${allSelected ? "ccuMs__row--on" : ""}`} onClick={() => onChange([])}>
            <span className={`ccuChk ${allSelected ? "ccuChk--on" : ""}`}>{allSelected && "✓"}</span>
            <span className="ccuMs__name">All members</span>
          </button>
          <div className="ccuMs__divider" />
          {members.map(m => {
            const checked = selected.includes(m.memberId);
            return (
              <button type="button" key={m.memberId} className={`ccuMs__row ${checked ? "ccuMs__row--on" : ""}`} onClick={() => toggle(m.memberId)}>
                <span className={`ccuChk ${checked ? "ccuChk--on" : ""}`}>{checked && "✓"}</span>
                <span className="ccuMs__avatar">{getInitials(m)}</span>
                <span className="ccuMs__name">{getFullName(m)}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const SkeletonRow = () => (
  <tr className="ccuTr">
    {[...Array(9)].map((_, i) => (
      <td key={i} className="ccuTd">
        <span className="ccuSkeleton" style={{ width: i === 0 ? 16 : `${60 + (i % 3) * 30}px`, height: 14 }} />
      </td>
    ))}
  </tr>
);

const CallDrawer = ({ call, members, onClose }) => {
  if (!call) return null;
  const member = members.find(m => m.memberId === call.memberId);
  const country = getCountryFromPhone(call.fromNumber);
  const contactLine = [call.contactName, call.contact?.company].filter(Boolean).join(" · ") || "—";
  const rows = [
    ["Contact", contactLine],
    ["Team member", member ? getFullName(member) : call.memberId || "—"],
    ["Number", call.fromNumber || "—"],
    ["Country", country ? country.name : "—", country],
    ["Date & time", `${fmtDate(call.createTs)}, ${fmtTime(call.createTs)}`],
    ["Duration", call.duration ? CallsUtils.secondsToTime(call.duration) : "—"],
    ["Call credits", call.callPrice != null ? `-$${Math.abs(parseFloat(call.callPrice)).toFixed(4)}` : "—"],
    ["Rec. credits", call.recordingPrice != null ? `-$${Math.abs(parseFloat(call.recordingPrice)).toFixed(4)}` : "—"]
  ];
  return (
    <div className="ccuDrawer__overlay" onClick={onClose}>
      <div className="ccuDrawer" onClick={e => e.stopPropagation()}>
        <div className="ccuDrawer__header">
          <div className="ccuDrawer__title">Call summary</div>
          <button className="ccuDrawer__close" onClick={onClose}>✕</button>
        </div>
        <div className="ccuDrawer__meta">
          {rows.map(([k, v, flagCountry]) => (
            <div key={k} className="ccuDrawer__row">
              <span className="ccuDrawer__key">{k}</span>
              <span className="ccuDrawer__val">
                {flagCountry ? <CountryFlag country={flagCountry} /> : null}
                {v}
              </span>
            </div>
          ))}
        </div>
        {call.notes && (
          <div className="ccuDrawer__notes">
            <span className="ccuDrawer__notesBadge">✦ AI Notes</span>
            <div className="ccuDrawer__notesText">{call.notes}</div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── main component ───────────────────────────────────────────────────────────

const CallCreditUsage = observer(() => {
  const [calls, setCalls] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [limit, setLimit] = useState(25);
  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState(null);
  const [summary, setSummary] = useState({ callCreditsUsed: 0, recordingCreditsUsed: 0 });
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [activeDrawer, setActiveDrawer] = useState(null);
  const [showFilter, setShowFilter] = useState(false);
  // Country filter options derived from the team's own numbers (stable — not from the
  // filtered call results, so they don't vanish when a member has no calls).
  const [numberCountries, setNumberCountries] = useState([]);
  const filterRef = useRef(null);

  // filter state
  const [datePreset, setDatePreset] = useState("Last 30 days");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [selectedCountries, setSelectedCountries] = useState([]);

  // active (applied) filters
  const [appliedFilters, setAppliedFilters] = useState({
    datePreset: "Last 30 days",
    customStart: "",
    customEnd: "",
    members: [],
    countries: []
  });

  const buildFilter = (f) => {
    const filter = {};
    const range = f.datePreset === "Custom"
      ? (f.customStart || f.customEnd ? { start: f.customStart, end: f.customEnd } : null)
      : getPresetRange(f.datePreset);
    // Backend PhoneCallFilter expects fromDate/to (Date). Send local day bounds.
    if (range) {
      if (range.start) filter.fromDate = `${range.start}T00:00:00`;
      if (range.end) filter.to = `${range.end}T23:59:59`;
    }
    if (f.members?.length) filter.memberIds = f.members;
    // Country filter → dial-code prefixes; backend matches fromNumber by prefix (no stored country field)
    if (f.countries?.length) {
      const codes = f.countries
        .map(name => countryData.allCountries.find(c => c.name === name)?.dialCode)
        .filter(Boolean);
      if (codes.length) filter.countryCodes = codes;
    }
    filter.all = true;
    return filter;
  };

  const fetchCalls = useCallback(async (page, f, pageLimit = limit) => {
    setLoading(true);
    try {
      const res = await makeApi({
        url: URLS.getCallsOverviewData,
        method: "POST",
        data: { pageNo: page, limit: pageLimit, filter: buildFilter(f), totalCount: page === 1 ? 0 : totalCount }
      });
      const data = res?.data || {};
      const callList = data.calls || [];
      setCalls(callList);
      // API returns the count as `totalCalls`
      setTotalCount(data.totalCalls || 0);
      // Accumulate country options from loaded calls (union — never shrinks)
      setNumberCountries(prev => {
        const map = new Map(prev.map(c => [c.name, c]));
        callList.forEach(call => {
          const c = getCountryFromPhone(call.fromNumber);
          if (c) map.set(c.name, c);
        });
        return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
      });
    } catch (e) { console.error("Failed to fetch calls", e); }
    finally { setLoading(false); }
  }, [totalCount, limit]);

  const fetchBalance = async () => {
    try {
      const res = await makeApi({ url: URLS.getCreditsUsage, method: "GET" });
      // /cm/data/credits returns dialerCreditsRemaining
      setBalance(res?.data?.dialerCreditsRemaining ?? null);
    } catch (e) { console.error("Failed to fetch balance", e); }
  };

  // Server-side credit totals for the filtered range (not page-local)
  const fetchSummary = async (f) => {
    setSummaryLoading(true);
    try {
      const res = await makeApi({
        url: URLS.getCreditUsageSummary,
        method: "POST",
        data: { filter: buildFilter(f) }
      });
      const s = res?.data?.summary || {};
      setSummary({
        callCreditsUsed: s.callCreditsUsed || 0,
        recordingCreditsUsed: s.recordingCreditsUsed || 0
      });
    } catch (e) { console.error("Failed to fetch credit summary", e); }
    finally { setSummaryLoading(false); }
  };

  const fetchMembers = async () => {
    try {
      const res = await makeApi({ url: URLS.getTeamActiveList, method: "GET" });
      setMembers(res?.data || []);
    } catch (e) { console.error("Failed to fetch members", e); }
  };

  // Stable country options = countries of the team's own numbers (call-from countries)
  const fetchNumberCountries = async () => {
    try {
      const res = await makeApi({ url: URLS.getMyPhoneNumbers, method: "GET" });
      const nums = res?.data?.phoneNumbers || [];
      const map = new Map();
      nums.forEach(n => {
        const c = getCountryFromPhone(n.number);
        if (c) map.set(c.name, c);
      });
      setNumberCountries([...map.values()].sort((a, b) => a.name.localeCompare(b.name)));
    } catch (e) { console.error("Failed to fetch number countries", e); }
  };

  useEffect(() => {
    fetchBalance();
    fetchMembers();
    fetchNumberCountries();
    fetchCalls(1, appliedFilters);
    fetchSummary(appliedFilters);
  }, []);

  // close filter panel on outside click
  useEffect(() => {
    const handler = e => { if (filterRef.current && !filterRef.current.contains(e.target)) setShowFilter(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Sync the working (pending) filter state from what's currently applied, then open
  const openFilter = () => {
    setDatePreset(appliedFilters.datePreset);
    setCustomStart(appliedFilters.customStart);
    setCustomEnd(appliedFilters.customEnd);
    setSelectedMembers(appliedFilters.members);
    setSelectedCountries(appliedFilters.countries);
    setShowFilter(true);
  };

  const applyFilters = () => {
    const f = { datePreset, customStart, customEnd, members: selectedMembers, countries: selectedCountries };
    setAppliedFilters(f);
    setPageNo(1);
    fetchCalls(1, f);
    fetchSummary(f);
    setShowFilter(false);
  };

  // Discard pending changes (revert to applied) and close
  const cancelFilters = () => {
    setDatePreset(appliedFilters.datePreset);
    setCustomStart(appliedFilters.customStart);
    setCustomEnd(appliedFilters.customEnd);
    setSelectedMembers(appliedFilters.members);
    setSelectedCountries(appliedFilters.countries);
    setShowFilter(false);
  };

  const clearAllFilters = () => {
    setDatePreset("Last 30 days");
    setCustomStart("");
    setCustomEnd("");
    setSelectedMembers([]);
    setSelectedCountries([]);
    const f = { datePreset: "Last 30 days", customStart: "", customEnd: "", members: [], countries: [] };
    setAppliedFilters(f);
    setPageNo(1);
    fetchCalls(1, f);
    fetchSummary(f);
    setShowFilter(false);
  };

  // react-paginate callback (pageNoObj.selected is 0-indexed)
  const paginate = pageNoObj => {
    if (loading) return;
    const p = (pageNoObj?.selected ?? 0) + 1;
    setPageNo(p);
    setSelected(new Set());
    fetchCalls(p, appliedFilters);
  };

  const handleLimitChange = (newLimit = 25) => {
    setLimit(newLimit);
    setPageNo(1);
    setSelected(new Set());
    fetchCalls(1, appliedFilters, newLimit);
  };

  // row selection
  const allChecked = selected.size === calls.length && calls.length > 0;
  const someChecked = selected.size > 0 && selected.size < calls.length;
  const toggleRow = id => setSelected(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
  const toggleAll = () => setSelected(allChecked ? new Set() : new Set(calls.map(c => c.id || c._id)));

  // active filter chips
  const chips = [];
  if (appliedFilters.datePreset !== "Last 30 days") chips.push({ key: "date", label: appliedFilters.datePreset === "Custom" ? `${appliedFilters.customStart} → ${appliedFilters.customEnd}` : appliedFilters.datePreset });
  appliedFilters.members.forEach(id => { const m = members.find(x => x.memberId === id); if (m) chips.push({ key: `m_${id}`, label: getFullName(m) }); });
  appliedFilters.countries.forEach(c => chips.push({ key: `c_${c}`, label: c }));

  const removeChip = key => {
    let f = { ...appliedFilters };
    if (key === "date") { f.datePreset = "Last 30 days"; f.customStart = ""; f.customEnd = ""; setDatePreset("Last 30 days"); setCustomStart(""); setCustomEnd(""); }
    else if (key.startsWith("m_")) { const id = key.slice(2); f.members = f.members.filter(x => x !== id); setSelectedMembers(f.members); }
    else if (key.startsWith("c_")) { const c = key.slice(2); f.countries = f.countries.filter(x => x !== c); setSelectedCountries(f.countries); }
    setAppliedFilters(f);
    setPageNo(1);
    fetchCalls(1, f);
    fetchSummary(f);
  };


  const handleExport = () => {
    const rows = selected.size > 0 ? calls.filter(c => selected.has(c.id || c._id)) : calls;
    if (!rows.length) return;
    const headers = ["Member", "Contact", "Company", "From Number", "Country", "Date & Time", "Duration", "Call Credits", "Rec. Credits", "Notes"];
    const data = rows.map(c => {
      const member = members.find(m => m.memberId === c.memberId);
      const country = getCountryFromPhone(c.fromNumber);
      return [
        member ? getFullName(member) : c.memberId || "",
        c.contactName || "",
        c.contact?.company || "",
        c.fromNumber || "",
        country?.name || "",
        formatDateTime(c.createTs),
        c.duration ? CallsUtils.secondsToTime(c.duration) : "",
        c.callPrice != null ? `$${parseFloat(c.callPrice).toFixed(4)}` : "",
        c.recordingPrice != null ? `$${parseFloat(c.recordingPrice).toFixed(4)}` : "",
        c.notes || ""
      ];
    });
    exportCSV([headers, ...data], "call_credit_usage.csv");
  };

  const totalPages = Math.ceil(totalCount / limit);
  const rangeLabel = appliedFilters.datePreset === "Custom" && appliedFilters.customStart && appliedFilters.customEnd
    ? `${appliedFilters.customStart} – ${appliedFilters.customEnd}`
    : appliedFilters.datePreset;

  return (
    <div className="ccuPage">
      {activeDrawer && <CallDrawer call={activeDrawer} members={members} onClose={() => setActiveDrawer(null)} />}

      {/* Summary cards */}
      <div className="ccuCards">
        <SummaryCard label="Call credits used"
          value={summaryLoading ? "—" : `$${summary.callCreditsUsed.toFixed(4)}`} sub={rangeLabel} />
        <SummaryCard label="Recording credits used"
          value={summaryLoading ? "—" : `$${summary.recordingCreditsUsed.toFixed(4)}`} sub={`${rangeLabel} · billed separately`} />
        <SummaryCard label="Total calls"
          value={loading ? "—" : totalCount.toLocaleString()} sub={rangeLabel} />
        <SummaryCard label="Balance"
          value={balance != null ? `$${parseFloat(balance).toFixed(4)}` : "—"}
          sub="call credits remaining" />
      </div>

      {/* Notice bar */}
      <div className="ccuNotice">
        <i className="material-icons-outlined">schedule</i>
        <span>Call and recording credits are billed in <strong>1-minute increments</strong> — a 10-second call uses the same credits as a full minute.</span>
      </div>

      {/* Toolbar */}
      <div className="ccuToolbar">
        <div className="ccuToolbar__left">
          {chips.map(chip => (
            <span key={chip.key} className="ccuChip">
              {chip.label}
              <button className="ccuChip__dismiss" onClick={() => removeChip(chip.key)}>×</button>
            </span>
          ))}
        </div>
        <div className="ccuToolbar__right">
          <div className="ccuToolbar__limit">
            <PageLimit
              limitPerPage={limit}
              text="Show"
              cbk={handleLimitChange}
              totalCount={totalCount}
            />
          </div>
          <div className="ccuFilterWrap" ref={filterRef}>
            <button className={`ccuBtn ${showFilter ? "ccuBtn--active" : ""}`} onClick={() => showFilter ? setShowFilter(false) : openFilter()}>
              <i className="material-icons-outlined" style={{ fontSize: 14 }}>tune</i>
              Filters
              {chips.length > 0 && <span className="ccuBadge">{chips.length}</span>}
            </button>
            {showFilter && (
              <div className="ccuPanel">
                {/* Date range */}
                <div className="ccuPanel__section">
                  <div className="ccuPanel__label">Date range</div>
                  <div className="ccuPanel__dateTabs">
                    {DATE_PRESETS.map(p => (
                      <button key={p} className={`ccuPanel__dateTab ${datePreset === p ? "ccuPanel__dateTab--active" : ""}`} onClick={() => setDatePreset(p)}>{p}</button>
                    ))}
                  </div>
                  {datePreset === "Custom" && (
                    <div className="ccuPanel__dateInputs">
                      <input type="date" className="ccuPanel__dateInput" value={customStart} onChange={e => setCustomStart(e.target.value)} />
                      <span className="ccuPanel__dateSep">–</span>
                      <input type="date" className="ccuPanel__dateInput" value={customEnd} onChange={e => setCustomEnd(e.target.value)} />
                    </div>
                  )}
                </div>
                <div className="ccuPanel__divider" />
                {/* Team member — multi-select dropdown */}
                <div className="ccuPanel__section">
                  <div className="ccuPanel__label">Team member</div>
                  <MemberMultiSelect
                    members={members}
                    selected={selectedMembers}
                    onChange={setSelectedMembers}
                  />
                </div>
                <div className="ccuPanel__divider" />
                {/* Country filter */}
                <div className="ccuPanel__section">
                  <div className="ccuPanel__label">Country</div>
                  {numberCountries.length > 0 ? (
                    <div className="ccuPanel__countryList">
                      {numberCountries.map(c => {
                        const checked = selectedCountries.includes(c.name);
                        return (
                          <button key={c.name} className={`ccuPanel__countryRow ${checked ? "ccuPanel__countryRow--on" : ""}`} onClick={() => setSelectedCountries(prev => checked ? prev.filter(x => x !== c.name) : [...prev, c.name])}>
                            <span className={`ccuChk ${checked ? "ccuChk--on" : ""}`}>{checked && "✓"}</span>
                            <CountryFlag country={c} />
                            <span className="ccuPanel__countryName">{c.name}</span>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="ccuPanel__empty">No countries available</div>
                  )}
                </div>
                <div className="ccuPanel__divider" />
                <div className="ccuPanel__footer">
                  <button className="ccuPanel__clear" onClick={clearAllFilters}>Clear all</button>
                  <div className="ccuPanel__footerBtns">
                    <button className="ccuBtn" onClick={cancelFilters}>Cancel</button>
                    <button className="ccuBtn ccuBtn--primary" onClick={applyFilters}>Apply filters</button>
                  </div>
                </div>
              </div>
            )}
          </div>
          <button className="ccuBtn" onClick={handleExport} disabled={calls.length === 0}>
            <i className="material-icons-outlined" style={{ fontSize: 14 }}>download</i>
            Export CSV{selected.size > 0 ? ` (${selected.size})` : ""}
          </button>
        </div>
      </div>

      {/* Table area: scrollable body + attached pagination footer */}
      <div className="ccuTableArea">
      <div className="ccuScroll">
      <div className="ccuTable__wrap">
        <table className="ccuTable">
          <thead>
            <tr>
              <th className="ccuTh ccuTh--check">
                <input type="checkbox" className="ccuCheckbox" checked={allChecked} ref={el => { if (el) el.indeterminate = someChecked; }} onChange={toggleAll} />
              </th>
              <th className="ccuTh">Team member</th>
              <th className="ccuTh">Contact</th>
              <th className="ccuTh">From number</th>
              <th className="ccuTh">Country</th>
              <th className="ccuTh">Date &amp; time</th>
              <th className="ccuTh ccuTh--right">Duration</th>
              <th className="ccuTh ccuTh--right">Call credits</th>
              <th className="ccuTh ccuTh--right">Rec. credits</th>
              <th className="ccuTh" />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              [...Array(8)].map((_, i) => <SkeletonRow key={i} />)
            ) : calls.length === 0 ? (
              <tr>
                <td colSpan={10} className="ccuTd ccuTd--empty">
                  <i className="material-icons-outlined" style={{ fontSize: 32, color: "#d1d5db", display: "block", marginBottom: 8 }}>phone_missed</i>
                  No calls found for the selected filters
                </td>
              </tr>
            ) : calls.map(c => {
              const id = c.id || c._id;
              const member = members.find(m => m.memberId === c.memberId);
              const country = getCountryFromPhone(c.fromNumber);
              const isSelected = selected.has(id);
              return (
                <tr key={id} className={`ccuTr ${isSelected ? "ccuTr--selected" : ""}`}>
                  <td className="ccuTd ccuTd--check">
                    <input type="checkbox" className="ccuCheckbox" checked={isSelected} onChange={() => toggleRow(id)} />
                  </td>
                  <td className="ccuTd">
                    <div className="ccuMember">
                      <div className="ccuAvatar">{member ? getInitials(member) : "?"}</div>
                      <span className="ccuMember__name">{member ? getFullName(member) : c.memberId || "—"}</span>
                    </div>
                  </td>
                  <td className="ccuTd">
                    <div className="ccuContact">{c.contactName || "—"}</div>
                    {c.contact?.company && <div className="ccuSub">{c.contact.company}</div>}
                  </td>
                  <td className="ccuTd ccuMono">{c.fromNumber || "—"}</td>
                  <td className="ccuTd">
                    {country ? (
                      <div className="ccuCountry">
                        <CountryFlag country={country} />
                        <div>
                          <span className="ccuCountry__name">{country.name}</span>
                          <span className="ccuCountry__dial">+{country.dialCode}</span>
                        </div>
                      </div>
                    ) : "—"}
                  </td>
                  <td className="ccuTd">
                    <div className="ccuDate">{fmtDate(c.createTs)}</div>
                    <div className="ccuTime">{fmtTime(c.createTs)}</div>
                  </td>
                  <td className="ccuTd ccuTd--right ccuDuration">{c.duration ? CallsUtils.secondsToTime(c.duration) : "—"}</td>
                  <td className="ccuTd ccuTd--right">
                    <div className="ccuCredit">{c.callPrice != null ? `-$${Math.abs(parseFloat(c.callPrice)).toFixed(4)}` : "—"}</div>
                    <div className="ccuCreditLabel">call</div>
                  </td>
                  <td className="ccuTd ccuTd--right">
                    <div className="ccuCredit ccuCredit--rec">{c.recordingPrice != null ? `-$${Math.abs(parseFloat(c.recordingPrice)).toFixed(4)}` : "—"}</div>
                    <div className="ccuCreditLabel">recording</div>
                  </td>
                  <td className="ccuTd ccuTd--action">
                    <button className="ccuNotesBtn" title="View call summary" onClick={() => setActiveDrawer(c)}>
                      ✦ Notes
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      </div>

      {/* Pagination — attached below the table */}
      <div className="ccuFooterBar">
        <PaginationComponent
          callback={paginate}
          loading={loading}
          totalPages={totalPages}
          currentPage={pageNo}
        />
      </div>
      </div>
    </div>
  );
});

export default CallCreditUsage;
